import { Link, useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import {
  FaShoppingCart,
  FaUser,
  FaSignOutAlt,
  FaSignInAlt,
  FaPills,
  FaUserNurse,
  FaBars,
  FaTimes,
  FaVial,
  FaUserMd,
  FaCalendarCheck,
  FaVideo,
  FaHistory
} from 'react-icons/fa';
import logo from '../../assets/logo.jpg';
import NotificationBell from '../notifications/NotificationBell';

export default function Header() {
  const navigate = useNavigate();
  const { token, logout } = useContext(AuthContext);
  const { cartCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile menu when clicking outside or on a link
  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      {/* TOP NAV */}
      <nav 
        className="flex justify-between items-center px-4 py-3 text-white h-15 relative shadow-md"
        style={{ backgroundColor: '#104a5f' }}
      >
        {/* LEFT SIDE: Logo */}
        <div className="flex items-center">
          <Link to="/" onClick={closeMobileMenu}>
            <img 
              src={logo} 
              alt="MedApp Logo" 
              className="h-9 max-w-36 md:max-w-44 object-contain"
            />
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <div 
          onClick={() => setMenuOpen(!menuOpen)} 
          className="md:hidden cursor-pointer p-2 flex items-center justify-center rounded transition-colors duration-200 hover:bg-white hover:bg-opacity-10"
          style={{ backgroundColor: menuOpen ? 'rgba(255,255,255,0.1)' : 'transparent' }}
        >
          {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </div>

        {/* Desktop Menu - Cart, Profile, Login/Logout */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/online-consultation"
            className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10"
          >
            <FaVideo size={16} />
            <span>Video Consult</span>
          </Link>
          <Link
            to="/order-history"
            className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10"
          >
            <FaHistory size={16} />
            <span>Orders</span>
          </Link>
          {token && <NotificationBell />}
          <Link
            to="/cart"
            className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10 relative"
          >
            <FaShoppingCart size={16} />
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Link>
          <Link 
            to="/profile" 
            className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10"
          >
            <FaUser size={16} />
            <span>Profile</span>
          </Link>
          {token ? (
            <span 
              onClick={logout} 
              className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10 cursor-pointer"
            >
              <FaSignOutAlt size={16} />
              <span>Logout</span>
            </span>
          ) : (
            <Link 
              to="/login" 
              className="flex items-center gap-2 text-white text-sm font-medium px-3 py-2 rounded-md transition-all duration-200 hover:bg-white hover:bg-opacity-10"
            >
              <FaSignInAlt size={16} />
              <span>Login</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      <div 
        className="md:hidden text-white overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          backgroundColor: '#104a5f',
          maxHeight: menuOpen ? '400px' : '0',
          borderTop: menuOpen ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}
      >
        <div className="flex flex-col gap-3" style={{ padding: menuOpen ? '16px' : '0 16px' }}>
          <Link
            to="/online-consultation"
            className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10"
            onClick={closeMobileMenu}
          >
            <FaVideo size={18} />
            <span>Video Consultation</span>
          </Link>
          <Link
            to="/order-history"
            className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10"
            onClick={closeMobileMenu}
          >
            <FaHistory size={18} />
            <span>Order History</span>
          </Link>
          <Link
            to="/order-step-1"
            className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10"
            onClick={closeMobileMenu}
          >
            <FaShoppingCart size={18} />
            <span>Cart</span>
          </Link>
          <Link 
            to="/profile" 
            className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10"
            onClick={closeMobileMenu}
          >
            <FaUser size={18} />
            <span>Profile</span>
          </Link>
          {token ? (
            <span 
              onClick={() => { logout(); closeMobileMenu(); }} 
              className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10 cursor-pointer"
            >
              <FaSignOutAlt size={18} />
              <span>Logout</span>
            </span>
          ) : (
            <Link 
              to="/login" 
              className="flex items-center gap-3 text-white text-base font-medium py-3 px-2 rounded-md transition-colors duration-200 border-b border-white border-opacity-10 hover:bg-white hover:bg-opacity-10"
              onClick={closeMobileMenu}
            >
              <FaSignInAlt size={18} />
              <span>Login</span>
            </Link>
          )}
        </div>
      </div>

      {/* SERVICES ROW - ALWAYS HORIZONTAL, ALWAYS VISIBLE */}
      <div 
        className="flex items-center justify-evenly p-2 min-h-12 shadow-md gap-1"
        style={{ backgroundColor: '#0d3b4a' }}
      >
        
        <Link 
          to="/medicines" 
          className="flex flex-col items-center gap-1 text-white text-center font-semibold py-1.5 px-1 rounded-md transition-colors duration-200 flex-1 hover:bg-white hover:bg-opacity-10"
        >
          <FaPills className="text-xs md:text-base" />
          <span className="text-xs md:text-sm">Medicine</span>
        </Link>
        
        <Link 
          to="/paramedical" 
          className="flex flex-col items-center gap-1 text-white text-center font-semibold py-1.5 px-1 rounded-md transition-colors duration-200 flex-1 hover:bg-white hover:bg-opacity-10"
        >
          <FaUserNurse className="text-xs md:text-base" />
          <span className="text-xs md:text-sm">Paramedical</span>
        </Link>
        
        <Link 
          to="/medical-tests" 
          className="flex flex-col items-center gap-1 text-white text-center font-semibold py-1.5 px-1 rounded-md transition-colors duration-200 flex-1 hover:bg-white hover:bg-opacity-10"
        >
          <FaVial className="text-xs md:text-base" />
          <span className="text-xs md:text-sm">Test</span>
        </Link>
        
        <Link 
          to="/online-consultation" 
          className="flex flex-col items-center gap-1 text-white text-center font-semibold py-1.5 px-1 rounded-md transition-colors duration-200 flex-1 hover:bg-white hover:bg-opacity-10"
        >
          <FaVideo className="text-xs md:text-base" />
          <span className="text-xs md:text-sm">Video Call</span>
        </Link>
        
        <Link 
          to="/doctor-list" 
          className="flex flex-col items-center gap-1 text-white text-center font-semibold py-1.5 px-1 rounded-md transition-colors duration-200 flex-1 hover:bg-white hover:bg-opacity-10"
        >
          <FaCalendarCheck className="text-xs md:text-base" />
          <span className="text-xs md:text-sm">Appointment</span>
        </Link>
      </div>
    </>
  );
}